import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Gamepad2, Film, X, Image, Info } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  
  const [contentType, setContentType] = useState('game');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer?.files[0] || e.target.files[0];
    if (!droppedFile) return;

    const ext = droppedFile.name.split('.').pop().toLowerCase();
    
    if (contentType === 'game' && ext !== 'swf') {
      toast.error('Games must be .swf files');
      return;
    }
    
    if (contentType === 'video' && !['mp4', 'webm'].includes(ext)) {
      toast.error('Videos must be .mp4 or .webm files');
      return;
    }

    setFile(droppedFile);
    toast.success(`File selected: ${droppedFile.name}`);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Thumbnail must be an image');
      return;
    }

    setThumbnail(file);
    
    const reader = new FileReader();
    reader.onload = (e) => setThumbnailPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('content_type', contentType);
      formData.append('tags', tags);
      formData.append('file', file);
      
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
      }

      const response = await fetch(`${API}/content/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const content = await response.json();
        toast.success('Upload successful! Your content is now in The Vortex.');
        navigate('/vortex');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="upload-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Upload size={32} className="text-[#cc0000]" />
        <div>
          <h1 className="text-2xl font-black text-white">UPLOAD</h1>
          <p className="text-xs text-[#666]">Submit your creation to The Vortex</p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-[#1a1a00] border border-[#333300]">
        <Info size={20} className="text-[#ffcc00] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#cccc00]">
          <p className="font-bold mb-1">THE VORTEX JUDGMENT SYSTEM</p>
          <p>
            Your submission will enter The Vortex for 24 hours. During this time, users can vote 
            on it. If your content maintains a rating of 2.0 or higher, it survives and becomes 
            part of WarSyndicate. Below 2.0? <span className="text-[#ff0000]">BLAMMED!</span>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Content type selector */}
        <div>
          <label className="text-xs text-[#666] uppercase mb-2 block">Content Type</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                setContentType('game');
                setFile(null);
              }}
              className={`p-4 border transition-colors flex items-center justify-center gap-2 ${
                contentType === 'game' 
                  ? 'bg-[#cc0000] border-[#990000] text-white' 
                  : 'bg-[#111] border-[#333] text-[#999] hover:border-[#666]'
              }`}
              data-testid="type-game"
            >
              <Gamepad2 size={20} />
              <span className="font-bold uppercase">Game (.swf)</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setContentType('video');
                setFile(null);
              }}
              className={`p-4 border transition-colors flex items-center justify-center gap-2 ${
                contentType === 'video' 
                  ? 'bg-[#cc0000] border-[#990000] text-white' 
                  : 'bg-[#111] border-[#333] text-[#999] hover:border-[#666]'
              }`}
              data-testid="type-video"
            >
              <Film size={20} />
              <span className="font-bold uppercase">Movie (.mp4/.webm)</span>
            </button>
          </div>
        </div>

        {/* File upload zone */}
        <div>
          <label className="text-xs text-[#666] uppercase mb-2 block">File</label>
          <div
            className={`ws-upload-zone ${dragOver ? 'dragover' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="file-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                {contentType === 'game' ? <Gamepad2 size={24} className="text-[#44ff44]" /> : <Film size={24} className="text-[#44ff44]" />}
                <span className="text-[#44ff44] mono">{file.name}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="p-1 hover:bg-white/10"
                >
                  <X size={16} className="text-[#666]" />
                </button>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto mb-2 text-[#444]" />
                <p className="text-[#666] mb-1">
                  Drag & drop your {contentType === 'game' ? '.swf file' : '.mp4/.webm file'} here
                </p>
                <p className="text-xs text-[#444]">or click to browse</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={contentType === 'game' ? '.swf' : '.mp4,.webm'}
              onChange={handleFileDrop}
              className="hidden"
              data-testid="file-input"
            />
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs text-[#666] uppercase mb-2 block">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a title..."
            className="bg-black border-[#333] text-white focus:border-[#cc0000]"
            data-testid="upload-title"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs text-[#666] uppercase mb-2 block">Description</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your content..."
            className="bg-black border-[#333] text-white focus:border-[#cc0000] min-h-[100px]"
            data-testid="upload-description"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="text-xs text-[#666] uppercase mb-2 block">Tags (comma separated)</label>
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="action, arcade, shooter..."
            className="bg-black border-[#333] text-white focus:border-[#cc0000]"
            data-testid="upload-tags"
          />
        </div>

        {/* Custom thumbnail */}
        <div>
          <label className="text-xs text-[#666] uppercase mb-2 block">
            Custom Thumbnail (Optional - 300x300)
          </label>
          <div className="flex items-start gap-4">
            <div
              className="w-32 h-32 border border-dashed border-[#333] flex items-center justify-center cursor-pointer hover:border-[#cc0000] transition-colors bg-[#0a0a0a]"
              onClick={() => thumbnailInputRef.current?.click()}
              data-testid="thumbnail-zone"
            >
              {thumbnailPreview ? (
                <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <Image size={24} className="mx-auto mb-1 text-[#444]" />
                  <p className="text-[10px] text-[#444]">Upload</p>
                </div>
              )}
            </div>
            <div className="text-xs text-[#666]">
              <p>If not provided, a thumbnail will be auto-generated.</p>
              <p className="mt-1">For games: placeholder image</p>
              <p>For videos: frame capture at 1 second</p>
              {thumbnail && (
                <button
                  type="button"
                  onClick={() => { setThumbnail(null); setThumbnailPreview(null); }}
                  className="mt-2 text-[#cc0000] hover:text-[#ff3333]"
                >
                  Remove custom thumbnail
                </button>
              )}
            </div>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="hidden"
              data-testid="thumbnail-input"
            />
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={uploading || !file}
          className="w-full btn-primary py-3 text-sm disabled:opacity-50"
          data-testid="upload-submit"
        >
          {uploading ? 'UPLOADING...' : 'SUBMIT TO THE VORTEX'}
        </button>
      </form>
    </div>
  );
}
